import type { MutableRefObject } from "react";
import { useDevAgentStore } from "@/stores/devAgentStore";
import toast from "react-hot-toast";
import { createDevAgentSession, sendDevAgentMessage } from "../devAgentService";
import type { useMetadataEditorState } from "../components/useMetadataEditorState";

export type RunDevAgentSendContext = {
  userId: string | undefined;
  orgId: string | undefined;
  sessionUuid: string | null;
  setSessionUuid: (uuid: string) => void;
  connectSSE: (uuid: string) => void;
  forceReconnect: (uuid: string) => void;
  refreshSessions: () => void;
  editor: ReturnType<typeof useMetadataEditorState>;
  sendingRef: MutableRefObject<boolean>;
};

const CREATE_INTENT_WORDS = new Set([
  "create",
  "build",
  "make",
  "generate",
  "write",
  "implement",
  "develop",
]);

const METADATA_NAME_STOP_WORDS = new Set([
  "The",
  "And",
  "For",
  "With",
  "This",
  "That",
  "From",
  "What",
  "How",
  "Why",
  "Can",
  "Will",
  "All",
  "Are",
  "Was",
  "Has",
  "Had",
  "Not",
  "But",
  "Our",
  "Its",
  "Get",
  "Set",
  "Let",
  "Var",
  "New",
  "Use",
  "Should",
  "Salesforce",
  "Webhook",
  "Batch",
]);

const METADATA_NAME_HINT_RE =
  /(?:Controller|Handler|Trigger|Helper|Service|Batch|Class|Cls|Test|Utility|Manager|Client|Factory|Job|Processor)$/;

function extractLikelyMetadataNames(message: string): string[] {
  return message
    .split(/\s+/)
    .map((word) => word.replace(/[(),:?]/g, "").replace(/\.(cls|trigger)$/i, "").trim())
    .filter((word) => word.length > 3 && /^[A-Z][A-Za-z0-9_]*$/.test(word))
    .filter((word) => !METADATA_NAME_STOP_WORDS.has(word))
    .filter((word) => word.includes("_") || METADATA_NAME_HINT_RE.test(word))
    .sort((a, b) => b.length - a.length)
    .slice(0, 3);
}

/**
 * Core send path for dev agent chat: optimistic feed, export intercept, entity-open hint,
 * pending-file enrichment, session create, POST + SSE reconnect guard.
 */
export async function runDevAgentSend(
  message: string,
  displayText: string | undefined,
  ctx: RunDevAgentSendContext,
): Promise<void> {
  const {
    userId,
    orgId,
    sessionUuid,
    setSessionUuid,
    connectSSE,
    forceReconnect,
    refreshSessions,
    editor,
    sendingRef,
  } = ctx;

  if (!userId) return;
  if (sendingRef.current) return;
  sendingRef.current = true;

  const msg = message.trim();

  // Render the short `displayText` in the chat bubble when provided —
  // backend still gets the full `message`. Used by Improve picker so the
  // user sees "Improve coverage for Foo" instead of a wall of line numbers.
  useDevAgentStore.getState().addFeedItem({ type: "chat_user", data: { content: displayText || message } });

  // NOTE: there used to be an export-intent intercept here that scanned the
  // message for words like "export"/"doc"/"csv" and tried to download a file
  // client-side instead of sending the message to the LLM. It's gone — it
  // caused false positives like "analyze this doc" downloading a docx,
  // and the proper download path now lives in the chat header bar buttons,
  // the per-message download bar, and the Monaco code-block buttons.

  const _isCreateIntent = msg.split(/\s+/).some((w) => CREATE_INTENT_WORDS.has(w.toLowerCase()));
  const _isLongOrTable = msg.length > 300 || msg.includes("|---");
  if (orgId && !_isCreateIntent && !_isLongOrTable) {
    const words = extractLikelyMetadataNames(msg);
    if (words.length > 0) {
      (async () => {
        try {
          const { getMetadataItemByName } = await import("../devAgentService");
          const sorted = [...words].sort((a, b) => b.length - a.length);
          for (const word of sorted) {
            try {
              const item = await getMetadataItemByName(word, orgId);
              if (item?.id) {
                editor.openTab({
                  id: item.id,
                  name: word,
                  metadataType: item.metadata_type || "ApexClass",
                });
                useDevAgentStore.getState().setEditorOpen(true);
                break;
              }
            } catch {
              /* not found, try next */
            }
          }
        } catch {
          /* non-fatal */
        }
      })();
    }
  }

  const pending = useDevAgentStore.getState().pendingFile;
  let enrichedMessage = message;
  // Attached file payload: send via `file_input` instead of stuffing
  // the raw body into `user_query`. Two sources:
  //   • Regular upload — we have `content`, no SF metadata id.
  //   • Monaco / Workspace click — we have `metadataId`, backend can
  //     retrieve the body via retrieve_apex; skip content to avoid the
  //     200K user_query cap.
  // Prefer id when available; fall back to content.
  const fileInput: Array<{ name: string; content?: string; metadataType?: string; id?: string }> = [];
  if (pending) {
    const fileId = String(pending.id);
    const fromInitial =
      editor.initialContent[fileId] || editor.initialContent[pending.name];
    const vf =
      useDevAgentStore.getState().virtualFiles[fileId] ||
      useDevAgentStore.getState().virtualFiles[pending.name];
    const fromVirtual = vf?.content;
    const fromPending = pending.content;
    const body =
      typeof fromInitial === "string" && fromInitial.length > 0
        ? fromInitial
        : typeof fromVirtual === "string" && fromVirtual.length > 0
          ? fromVirtual
          : typeof fromPending === "string" && fromPending.length > 0
            ? fromPending
            : null;

    // file_input.id must be EITHER an internal UUID (metadata_items row)
    // OR an `art_…` artifact id. SF 15/18-char IDs are never valid here
    // — the backend rejects them now. If the pending file only has an SF
    // id, fall through to content (full body) so the agent still works.
    const isUuid =
      typeof pending.id === "string"
      && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(pending.id);
    const isArtifactId =
      typeof pending.id === "string" && pending.id.startsWith("art_");
    const payload: { name: string; content?: string; metadataType?: string; id?: string } = {
      name: pending.name,
      metadataType: pending.metadataType,
    };
    if (isUuid || isArtifactId) {
      payload.id = String(pending.id);
      // Only include content when we have local-only edits that SF
      // doesn't know about yet (virtual files / unsaved modifications).
      if (fromVirtual && fromVirtual !== fromInitial) payload.content = fromVirtual;
    } else if (body) {
      payload.content = body;
    }
    fileInput.push(payload);
    // Keep a minimal breadcrumb in user_query so the LLM knows the file
    // is attached but isn't dragging the body into the prompt text.
    enrichedMessage = `[File: ${pending.name}${pending.metadataType ? ` (${pending.metadataType})` : ""}]\n\n${message}`;
    useDevAgentStore.getState().setPendingFile(null);
  }

  try {
    let uuid = sessionUuid;
    if (!uuid) {
      const session = await createDevAgentSession(userId, orgId);
      uuid = session.uuid;
      if ((session as any).sse_ticket) useDevAgentStore.getState().setSseTicket((session as any).sse_ticket);
      setSessionUuid(uuid);
      refreshSessions();
    }
    connectSSE(uuid);
    // Show spinner immediately — don't wait for run_start SSE event
    useDevAgentStore.getState().setRunStatus("running");
    // Arm silence timer — if SSE never delivers events, recover from DB
    import("../sse/sseEventHandlers").then(({ armSilenceTimer }) => armSilenceTimer());
    const sendUuid = uuid;
    try {
      await sendDevAgentMessage(uuid, {
        user_query: enrichedMessage,
        org_id: orgId,
        ...(fileInput.length ? { file_input: fileInput } : {}),
      });
      setTimeout(() => {
        const s = useDevAgentStore.getState();
        if (s.runStatus !== "running" && !s.streamingContent) {
          forceReconnect(sendUuid);
        }
      }, 5000);
    } catch (postErr: any) {
      // POST failed — reset spinner and kill silence timer
      useDevAgentStore.getState().setRunStatus("idle");
      import("../sse/sseMutableState").then(({ sseState }) => {
        if (sseState.stuckTimer) {
          clearTimeout(sseState.stuckTimer);
          sseState.stuckTimer = null;
        }
      });
      toast.error(postErr?.message || "Failed to send message");
      throw postErr;
    }
  } catch (e: any) {
    useDevAgentStore.getState().setRunStatus("idle");
    toast.error(e?.message || "Failed to send message");
    throw e;
  } finally {
    sendingRef.current = false;
  }
}
